import { captureException } from "@sentry/react";

import { callWithTimeout, detectCookiesEnabled, parseCookies } from "./cookies";

declare global {
  interface Window {
    [index: string]: any;
    ym?: (...args: any[]) => void;
    _ymSet?: boolean;
    _ymInitialized?: boolean;
    _ymError?: Event;
  }
}

export async function parseYandexSessionData() {
  if (!import.meta.env.DOQ_YANDEX_METRIKA_KEY) return undefined;

  try {
    const cookies = parseCookies();
    let client_id = cookies["_ym_uid"];

    if (!client_id) {
      client_id = localStorage.getItem("_ym_uid") ?? undefined;
    }

    // Fallback
    if (!client_id && window.ym) {
      client_id = await callWithTimeout((callback) => {
        window.ym?.(
          import.meta.env.DOQ_YANDEX_METRIKA_KEY,
          "getClientID",
          callback,
        );
      });
    }

    if (!client_id) {
      throw new Error("Couldn't parse ym cookies");
    }

    return {
      // Local storage stores in the format "c_id" (with quotes symbols)
      client_id: client_id.trim().replace('"', ""),
    };
  } catch (error) {
    const cookies = parseCookies();
    captureException(error, {
      level: Object.keys(cookies).some((key) => key.startsWith("_ym"))
        ? "error"
        : "warning",
      tags: { analytics: "_ym-cookie-parsing" },
      extra: {
        error: {
          type: typeof error,
          message: error instanceof Error ? error.message : undefined,
          data: JSON.stringify(error),
        },
        data: cookies,
        isYmDefined: Boolean(window.ym),
        cookiesEnabled: await detectCookiesEnabled(),
        isRobot: window.isRobot,
        _ymSet: window._ymSet,
        _ymInitialized: window._ymInitialized,
        _ymError: window._ymError,
        _ymErrorMessage:
          window._ymError && "message" in window._ymError
            ? window._ymError.message
            : undefined,
        consent: window.Cookiebot?.consent,
      },
    });
  }
  return undefined;
}
(window as any).parseYandexSessionData = parseYandexSessionData;

export const useYm = () => {
  return {
    parseSessionData: parseYandexSessionData,
  };
};
